import React, { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import type { AgenteMonedaView, CreateMonedaDTO, MonedaFormato, WrapperResponse } from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import {
    CURRENCIES_PATH,
    errorMessage,
    FORMAT_OPTIONS,
    formatAmount,
    parseAmount,
    sanitizeDecimal,
} from '../common/currency-common'
import type { CurrenciesLocationState, FormMessage } from '../common/currency-common'

export interface CurrencyFormState {
    id: number | null
    moneda: string
    tipoCambio: string
    formato: MonedaFormato
}

export interface CurrencyOption {
    code: string
    label: string
    configured: boolean
}

const LOAD_ERROR = 'Error al cargar la página'
const SAVE_ERROR = 'Error al guardar la moneda'
const DELETE_ERROR = 'Error al eliminar la moneda'
const NOT_FOUND = 'No se encontró la moneda.'

const emptyState: CurrencyFormState = {
    id: null,
    moneda: '',
    tipoCambio: formatAmount(0),
    formato: FORMAT_OPTIONS[0].value,
}

const asFormato = (value?: string | null): MonedaFormato =>
    FORMAT_OPTIONS.find((option) => option.value === value)?.value || FORMAT_OPTIONS[0].value

const toFormState = (body: AgenteMonedaView): CurrencyFormState => ({
    id: body.id,
    moneda: body.abreviacion || '',
    tipoCambio: formatAmount(Number(body.tipoCambio) || 0),
    formato: asFormato(body.formato),
})

const toRequest = (state: CurrencyFormState): CreateMonedaDTO => ({
    moneda: state.moneda,
    tipoCambio: parseAmount(state.tipoCambio),
    formato: state.formato,
})

export const useCurrencyFormModel = () => {
    const history = useHistory()
    const location = useLocation()
    const { monedaId } = useParams()
    const backUrl: string = (location.state as CurrenciesLocationState | null)?.backUrl || CURRENCIES_PATH

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [message, setMessage] = useState<FormMessage | null>(null)
    const [catalog, setCatalog] = useState<AgenteMonedaView[]>([])
    const [state, setState] = useState<CurrencyFormState>(emptyState)

    useEffect(() => {
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                const allResponse: WrapperResponse<AgenteMonedaView[]> | null = await (await APIInvoke.invokeGET('/monedas/all')).json()
                if (!active) return
                if (!allResponse?.ok) throw new Error(allResponse?.message || LOAD_ERROR)
                setCatalog(allResponse.body || [])
                if (!monedaId) {
                    setState(emptyState)
                    return
                }
                const response: WrapperResponse<AgenteMonedaView | null> | null = await (await APIInvoke.invokeGET(`/monedas/${monedaId}`)).json()
                if (!active) return
                if (!response?.ok) throw new Error(response?.message || LOAD_ERROR)
                if (!response.body) throw new Error(NOT_FOUND)
                setState(toFormState(response.body))
            } catch (error) {
                console.error('Error al inicializar la página', error)
                if (active) setMessage({ type: 'error', message: errorMessage(error, LOAD_ERROR) })
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [monedaId])

    const isEdit = Boolean(monedaId)

    const options: CurrencyOption[] = catalog
        .filter((item) => item.abreviacion)
        .map((item) => ({
            code: item.abreviacion as string,
            label: `${item.abreviacion} · ${item.moneda || ''}${item.pais ? ` (${item.pais})` : ''}`,
            configured: item.agenteId != null && item.abreviacion !== state.moneda,
        }))
        .sort((left, right) => left.code.localeCompare(right.code))

    const selected = catalog.find((item) => item.abreviacion === state.moneda) || null

    const patch = (partial: Partial<CurrencyFormState>) => setState((current) => ({ ...current, ...partial }))

    const change = {
        moneda: (value: string) => patch({ moneda: value }),
        tipoCambio: (value: string) => patch({ tipoCambio: sanitizeDecimal(value, 2, 10) }),
        tipoCambioBlur: (value: string) => patch({ tipoCambio: formatAmount(parseAmount(value)) }),
        formato: (value: MonedaFormato) => patch({ formato: value }),
    }

    const save = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!state.moneda) return
        try {
            setSaving(true)
            setMessage(null)
            const request = toRequest(state)
            const response: WrapperResponse<AgenteMonedaView> = isEdit
                ? await (await APIInvoke.invokePUT('/monedas', request)).json()
                : await (await APIInvoke.invokePOST('/monedas', request)).json()
            if (!response.ok) {
                setMessage({ type: 'error', message: response.message || SAVE_ERROR })
                return
            }
            history.push({ pathname: CURRENCIES_PATH, state: { flash: 'Moneda guardada correctamente' } satisfies CurrenciesLocationState })
        } catch (error) {
            console.error('Error al guardar', error)
            setMessage({ type: 'error', message: SAVE_ERROR })
        } finally {
            setSaving(false)
        }
    }

    const deleteCurrency = async () => {
        if (!state.id || deleting) return
        try {
            setConfirmDelete(false)
            setDeleting(true)
            setMessage(null)
            const response: WrapperResponse<null> = await (await APIInvoke.invokeDELETE(`/monedas/${state.id}`)).json()
            if (!response.ok) {
                setMessage({ type: 'error', message: response.message || DELETE_ERROR })
                return
            }
            history.push({ pathname: CURRENCIES_PATH, state: { flash: 'Moneda eliminada correctamente' } satisfies CurrenciesLocationState })
        } catch (error) {
            console.error('Error al eliminar la moneda', error)
            setMessage({ type: 'error', message: DELETE_ERROR })
        } finally {
            setDeleting(false)
        }
    }

    return {
        form: state,
        options,
        selected,
        loading,
        saving,
        deleting,
        busy: loading || saving || deleting,
        busyLabel: deleting ? 'Eliminando moneda…' : loading ? 'Cargando moneda…' : 'Guardando moneda…',
        message,
        isEdit,
        title: isEdit ? 'Editar moneda' : 'Nueva moneda',
        backTo: backUrl,
        confirmDelete,
        example: FORMAT_OPTIONS.find((option) => option.value === state.formato)?.example || '',
        change,
        save,
        cancel: () => history.push(backUrl),
        deleteCurrency,
        askDelete: () => setConfirmDelete(true),
        cancelDelete: () => setConfirmDelete(false),
    }
}
